import WKT from 'ol/format/WKT';
import * as olExtent from 'ol/extent';
import axios from 'axios';
/* We can disable import/no-cycle here because we know that
  api only depends on the User module. */
// eslint-disable-next-line import/no-cycle
import api from '@/api';

const defaultState = () => ({
  notification: {
    oldTownGeocodeNum: null,
  },
  loading: false,
  townExtent: null,
  loError: null,
  daError: null,
  operatorError: null,
  lfError: null,
  fonDeleteError: null,
  importantReminders: {
    harvestReminders: {},
    roadConstructionReminders: {},
    waterCrossingReminders: {},
    generalReminders: {},
  },
  roles: {},
  errors: {
    overviewErrors: [],
    mappingErrors: [],
    activityDetailsErrors: [],
    contactsErrors: [],
  },
  certifications: [],
  showCertifiedModal: false,
  landownerReports: [],
  landownerReportsLoading: false,
  // Notification Amendments State
  amendments: [],
  amendmentsLoading: false,
  amendmentsEditing: false,
  amendmentsBusy: false,
  amendmentsSaving: false,
  amendmentsError: null,
  scratchSessionId: null,
  scratchAttachments: [],
});

const localState = defaultState();

const actions = {
  async getAmendments({ commit, state }) {
    commit('SET_AMENDMENTS_EDITING', false);
    commit('SET_AMENDMENTS_LOADING', true);
    await api.get(`/notification/${state.notification.id}/amendments`)
      .then((response) => {
        commit('SET_AMENDMENTS_LOADING', false);
        commit('SET_AMENDMENTS', response.data);
      }).catch((error) => {
        commit('SET_AMENDMENTS_ERROR', error.response ? error.response.data : error.message);
      });
  },
  async saveAmendments({ commit, dispatch, state }) {
    commit('SET_AMENDMENTS_BUSY', true);
    commit('SET_AMENDMENTS_SAVING', true);
    await api.post(`/notification/${state.notification.id}/amendments/${state.scratchSessionId}`)
      .then(() => {
        if (state.scratchAttachments.length > 0 && state.notification.statusId !== 5) {
          const updatedNotification = Object.assign({}, state.notification, { statusId: 5 });
          commit('SET_NOTIFICATION', { notification: updatedNotification, source: 'saveAmendments' });
        } else if (state.scratchAttachments.length === 0 && state.notification.statusId !== 3) {
          const updatedNotification = Object.assign({}, state.notification, { statusId: 3 });
          commit('SET_NOTIFICATION', { notification: updatedNotification, source: 'saveAmendments' });
        }
        commit('SET_SCRATCH_ATTACHMENTS', []);
        commit('SET_SCRATCH_SESSION', null);
        commit('SET_AMENDMENTS_BUSY', false);
        commit('SET_AMENDMENTS_SAVING', false);
        dispatch('getAmendments');
      }).catch((error) => {
        commit('SET_AMENDMENTS_ERROR', error.response ? error.response.data : error.message);
      });
  },
  async downloadAmendment({ commit, state }, id) {
    const amendment = state.amendments.find(x => x.id === id);
    if (!amendment) { return; }
    const requestData = {
      documentName: amendment.fileName,
    };
    await api.post(`/notification/${state.notification.id}/amendments/${amendment.id}/download`, requestData)
      .then((response) => {
        window.open(response.data, '_blank');
      }).catch((error) => {
        commit('SET_AMENDMENTS_ERROR', error.response ? error.response.data : error.message);
      });
  },
  async initAmendmentScratchSession({ commit, dispatch }, id) {
    commit('SET_AMENDMENTS_EDITING', true);
    commit('SET_AMENDMENTS_LOADING', true);
    commit('SET_AMENDMENTS_BUSY', true);
    await api.get(`/scratchdocuments/initialize/amendment/${id}`)
      .then(async (response) => {
        await commit('SET_SCRATCH_SESSION', response.data);
        commit('SET_AMENDMENTS_BUSY', false);
        await dispatch('getAmendmentScratchAttachments');
      }).catch((error) => {
        commit('SET_AMENDMENTS_ERROR', error.response ? error.response.data : error.message);
      });
  },
  async getAmendmentScratchAttachments({ commit, state }) {
    commit('SET_AMENDMENTS_LOADING', true);
    await api.get(`/scratchdocuments/${state.scratchSessionId}`)
      .then((response) => {
        // build attachments
        const attachments = response.data.map((item) => {
          const attachment = {
            id: item.documentId,
            fileName: item.documentName,
            size: item.contentLength,
            isLoaded: true,
            loadingValue: 100,
            uploadError: null,
          };
          return attachment;
        });
        commit('SET_SCRATCH_ATTACHMENTS', attachments);
        commit('SET_AMENDMENTS_LOADING', false);
      }).catch((error) => {
        commit('SET_AMENDMENTS_ERROR', error.response ? error.response.data : error.message);
      });
  },
  async clearAmendmentScratchSession({ commit, state }) {
    if (!state.scratchSessionId) return;
    await api.delete(`/scratchdocuments/${state.scratchSessionId}`)
      .then(() => {
        commit('SET_SCRATCH_ATTACHMENTS', []);
        commit('SET_SCRATCH_SESSION', null);
        commit('SET_AMENDMENTS_EDITING', false);
      }).catch((error) => {
        commit('SET_AMENDMENTS_ERROR', error.response ? error.response.data : error.message);
      });
  },
  async deleteScratchAmendment({ state, commit }, id) {
    commit('SET_AMENDMENTS_BUSY', true);
    const attachment = state.scratchAttachments.find(x => x.id === id);
    if (!attachment) { return; }
    await api.delete(`/scratchdocuments/${state.scratchSessionId}/${attachment.id}/${attachment.fileName}`)
      .then(() => {
        commit('REMOVE_SCRATCH_ATTACHMENT', id);
        commit('SET_AMENDMENTS_BUSY', false);
      }).catch((error) => {
        commit('SET_AMENDMENTS_ERROR', error.response ? error.response.data : error.message);
      });
  },
  async downloadScratchAmendment({ state, commit }, id) {
    const attachment = state.scratchAttachments.find(x => x.id === id);
    if (!attachment) { return; }
    const requestData = {
      documentId: attachment.id,
      documentName: attachment.fileName,
    };
    await api.post(`/scratchdocuments/urls/download/${state.scratchSessionId}`, requestData)
      .then((response) => {
        window.open(response.data, '_blank');
      }).catch((error) => {
        commit('SET_AMENDMENTS_ERROR', error.response ? error.response.data : error.message);
      });
  },
  getAmendmentUploadURLs({ state }, files) {
    const fileDetails = [];
    Array.from(Array(files.length).keys())
      .forEach((x) => {
        fileDetails.push({
          documentName: files[x].name,
          contentType: files[x].type,
        });
      });
    return api.post(`/scratchdocuments/urls/upload/${state.scratchSessionId}`, fileDetails);
  },
  async uploadAmendmentsToScratchSession({ commit, dispatch }, files) {
    commit('SET_AMENDMENTS_BUSY', true);
    await dispatch('getAmendmentUploadURLs', files)
      .then(async (response) => {
        const urls = response.data;

        // build attachments
        const attachments = [];
        // eslint-disable-next-line
        for (let i = 0; i < files.length; i++) {
          const attachment = {
            id: urls[i].documentId,
            fileName: files[i].name,
            size: files[i].size,
            isLoaded: false,
            loadingValue: 0,
            uploadError: null,
          };
          attachments.push(attachment);
        }

        await commit('SET_SCRATCH_ATTACHMENTS', attachments);

        const s3Axios = axios.create();

        const numRequests = urls.length;
        let requestsComplete = 0;

        urls.forEach((url, index) => {
          const file = files[index];
          s3Axios.put(url.url, file, {
            headers: {
              'Content-Type': file.type,
            },
            onUploadProgress: (progressEvent) => {
              const progress = (progressEvent.loaded / progressEvent.total) * 100;
              const data = {
                id: url.documentId,
                props: {
                  loadingValue: parseInt(Math.round(progress), 10),
                },
              };
              setTimeout(() => {
                commit('UPDATE_SCRATCH_ATTACHMENT', data);
              }, 50);
            },
          }).then(() => {
            requestsComplete += 1;
            const data = {
              id: url.documentId,
              props: {
                isLoaded: true,
              },
            };
            setTimeout(() => {
              if (requestsComplete === numRequests) {
                commit('SET_AMENDMENTS_BUSY', false);
              }
              commit('UPDATE_SCRATCH_ATTACHMENT', data);
            }, 1000);
          }).catch((error) => {
            requestsComplete += 1;
            const data = {
              id: url.documentId,
              props: {
                uploadError: error,
              },
            };
            setTimeout(() => {
              if (requestsComplete === numRequests) {
                commit('SET_AMENDMENTS_BUSY', false);
              }
              commit('UPDATE_SCRATCH_ATTACHMENT', data);
            }, 1000);
          });
        });
      }).catch((error) => {
        commit('SET_AMENDMENTS_ERROR', error);
      });
  },
  async getNotification({ commit, state, dispatch }, id) {
    if (!id) return;
    if (parseInt(id) !== state.notification.id) {
      dispatch('clearAmendmentScratchSession');
      state.notification.oldTownGeocodeNum = undefined;
      // state.notification = {
      //   oldTownGeocodeNum: undefined,
      //   ...notification
      // };
    };
    commit('SET_LOADING', true);
    await api.get(`/notification/${id}`).then((response) => {
      commit('SET_NOTIFICATION', { notification: response.data, source: 'getNotification' });
      commit('SET_LOADING', false);
    }).catch(() => {
      commit('SET_LOADING', false);
    });
  },
  async deleteDraft({ commit }, id) {
    if (!id) return;
    await api.delete(`/notification/${id}`).then(() => {
      commit('SET_FON_DELETE_ERROR', null);
    }).catch(() => {
      commit('SET_FON_DELETE_ERROR', true);
    });
  },

  unload({ commit }) {
    commit('RESET_STATE');
  },
  async createDraft({ commit }, { data, submittedCallback, componentName }) {
    await api.post('/notification/createDraft', data)
      .then(async (response) => {
        await api.get(`/notification/${response.data}`)
          .then((getResponse) => {
            commit('SET_NOTIFICATION', { notification: getResponse.data, source: componentName || 'createDraft' });
            if (submittedCallback) submittedCallback(getResponse.data.id);
          });
      });
  },
  async saveDraft({ dispatch }, { data, submittedCallback, componentName }) {
    await api.put('/notification/update', data)
      .then(async () => {
        await dispatch('fetchNoReload', componentName);
        //await dispatch({type:'fetchNoReload', payload: componentName});
        if (submittedCallback) submittedCallback(data.id);
      });
  },
  async updateLupcModalStatus({ dispatch }, updateInfo) {
    console.log('updateLupcModalStatus')
    await api.patch(`/notification/${updateInfo.notificationId}/updateLupcModalStatus/${updateInfo.status}`)
      .then(() => {
        dispatch('fetchNoReload', 'updateLupcModalStatus');
      })
      .catch((error) => {
        console.error('updateLupcModalStatus api.patch failed:', error);
      });
  },
  async submit({ commit, dispatch }, id) {
    await api.post(`/notification/${id}/submit`)
      .then(async () => {
        await dispatch('getNotification', id);
        commit('SET_SHOW_CERTIFIED_MODAL', true);
      });
  },
  async sendPartyUserEmail({ dispatch }, emailInfo) {
    const { notificationId, delinquentUserId } = emailInfo;
    await api.post(`/notification/${notificationId}/sendPartyUserEmail/${delinquentUserId}`)
      .then(async () => {
        dispatch('fetchCertifications');
      });
  },
  async certify({ dispatch, commit }, id) {
    await api.post(`/notification/${id}/certify`)
      .then(() => {
        dispatch('fetchCertifications');
        commit('SET_SHOW_CERTIFIED_MODAL', true);
      });
  },
  async decline({ dispatch }, id) {
    await api.post(`/notification/${id}/decline`)
      .then(() => {
        dispatch('fetchCertifications');
      })
      .catch((error) => {
        console.error('decline failed:', error);
      });
  },
  async withdraw({ dispatch }, id) {
    await api.post(`/notification/${id}/withdraw`)
      .then(async () => {
        await dispatch('getNotification', id);
        dispatch('fetchCertifications');
      });
  },
  async hideCertifiedModal({ commit }) {
    commit('SET_SHOW_CERTIFIED_MODAL', false);
  },
  async getTownExtent({ state, commit }) {
    await api.get(`towngeometry/${state.notification.townGeocodeNum}`)
      .then((response) => {
        commit(
          'SET_TOWN_EXTENT',
          new WKT().readFeature(response.data).getGeometry().getExtent(),
        );
      });
  },
  async updateDesignatedAgent({ commit, state }, { data }) {
    await api.patch(`notification/${state.notification.id}/updateDesignatedAgent`, data)
      .then((response) => {
        commit('SET_DESIGNATED_AGENT', response.data);
        commit('SET_DA_ERROR', null);
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) commit('SET_DA_ERROR', error.response.data);
        else throw error;
      });
  },
  async updateLandowner({ commit, state }, { data }) {
    await api.patch(`notification/${state.notification.id}/updateLandowner`, data)
      .then((response) => {
        commit('SET_LANDOWNER', response.data);
        commit('SET_LO_ERROR', null);
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) commit('SET_LO_ERROR', error.response.data);
        else throw error;
      });
  },
  async createManagedLandowner({ commit, state }, { data, submittedCallback }) {
    await api.patch(`notification/${state.notification.id}/createManagedLandowner`, data)
      .then((response) => {
        commit('SET_LANDOWNER', response.data);
        commit('SET_LO_ERROR', null);
        submittedCallback();
      })
      .catch((error) => {
        submittedCallback();
        if (error.response && error.response.status === 400) {
          commit('SET_LO_ERROR', error.response.data);
        } else {
          throw error;
        }
      });
  },
  async updateLicensedForester({ commit, state }, { data }) {
    await api.patch(`notification/${state.notification.id}/updateLicensedForester`, data)
      .then((response) => {
        commit('SET_LICENSED_FORESTER', response.data);
        commit('SET_LF_ERROR', null);
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) commit('SET_LF_ERROR', error.response.data);
        else throw error;
      });
  },
  async updateOwnership({ commit, state }, { data }) {
    await api.patch(`notification/${state.notification.id}/updateOwnership`, data)
      .then(() => {
        commit('SET_OWNERSHIP', data);
      });
  },
  async updateOperator({ commit, state }, { data }) {
    await api.patch(`notification/${state.notification.id}/updateOperator`, data)
      .then((response) => {
        commit('SET_OPERATOR', response.data);
        commit('SET_OPERATOR_ERROR', null);
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) commit('SET_OPERATOR_ERROR', error.response.data);
        else throw error;
      });
  },
  async removeDesignatedAgent({ commit, state }) {
    await api.patch(`notification/${state.notification.id}/removeDesignatedAgent`)
      .then(() => {
        commit('SET_DESIGNATED_AGENT', null);
        commit('SET_DA_ERROR', null);
      })
      .catch(error => commit('SET_DA_ERROR', error.response ? error.response.data : error.message));
  },
  async removeLandowner({ commit, state }) {
    const shouldRemoveOperatorOnSuccess = state.notification.landownerUser
      && state.notification.landownerUser.isManagedForestUser
      && state.notification.operatorUser
      && state.notification.operatorUser.isManagedForestUser;
    await api.patch(`notification/${state.notification.id}/removeLandowner`)
      .then(() => {
        commit('SET_LANDOWNER', null);
        commit('SET_LO_ERROR', null);
        if (shouldRemoveOperatorOnSuccess) {
          commit('SET_OPERATOR', null);
          commit('SET_OPERATOR_ERROR', null);
        }
      })
      .catch(error => commit('SET_LO_ERROR', error.response ? error.response.data : error.message));
  },
  async removeOperator({ commit, state }) {
    await api.patch(`notification/${state.notification.id}/removeOperator`)
      .then(() => {
        commit('SET_OPERATOR', null);
        commit('SET_OPERATOR_ERROR', null);
      })
      .catch(error => commit('SET_OPERATOR_ERROR', error.response ? error.response.data : error.message));
  },
  async removeLicensedForester({ commit, state }) {
    await api.patch(`notification/${state.notification.id}/removeLicensedForester`)
      .then(() => {
        commit('SET_LICENSED_FORESTER', null);
        commit('SET_LF_ERROR', null);
      })
      .catch(error => commit('SET_LF_ERROR', error.response ? error.response.data : error.message));
  },
  async fetchNoReload({ commit, state }, componentName) {
    // Do not update loading since the notification is not reloaded
    await api.get(`notification/${state.notification.id}`)
      .then((response) => {
        commit('SET_NOTIFICATION', { notification: response.data, source: componentName});
      });
  },
  async fetchImportantReminders({ commit, state }) {
    await api.get(`notification/${state.notification.id}/importantReminders`)
      .then((response) => {
        commit('SET_IMPORTANT_REMINDERS', response.data);
      });
  },
  async fetchRoles({ commit, state }) {
    await api.get(`notification/${state.notification.id}/roles`)
      .then((response) => {
        commit('SET_ROLES', response.data);
      });
  },
  async validate({ commit, state, getters }) {
    if (!getters.draft) return;
    await api.get(`notification/${state.notification.id}/validate`)
      .then((response) => {
        commit('SET_ERRORS', response.data);
      });
  },
  async fetchCertifications({ commit, state, dispatch }) {
    if (!state.notification.id) return;
    await api.get(`notification/${state.notification.id}/certifications`)
      .then((response) => {
        commit('SET_CERTIFICATIONS', response.data);
        dispatch('getNotification', state.notification.id);
      });
  },
  async fetchLandownerReports({ commit, state }) {
    commit('SET_LANDOWNER_REPORTS_LOADING', true);
    await api.get(`notification/${state.notification.id}/landownerReports`)
      .then((response) => {
        commit('SET_LANDOWNER_REPORTS', response.data);
        commit('SET_LANDOWNER_REPORTS_LOADING', false);
      });
  },
  async checkCertificationMethod({ state }, certificationMethodId) {
    if (!state.notification.id) {
      return {};
    }

    return api.post('/notification/checkCertificationMethod', { id: state.notification.id, certificationMethodId })
      .then(response => response.data);
  },
};

const mutations = {
  SET_AMENDMENTS_STATE(state, value) {
    state.amendmentState = value;
  },
  SET_AMENDMENTS_LOADING(state, value) {
    state.amendmentsLoading = value;
  },
  SET_AMENDMENTS_EDITING(state, value) {
    state.amendmentsEditing = value;
  },
  SET_AMENDMENTS_BUSY(state, value) {
    state.amendmentsBusy = value;
  },
  SET_AMENDMENTS_SAVING(state, value) {
    state.amendmentsSaving = value;
  },
  SET_AMENDMENTS(state, amendments) {
    state.amendments = amendments;
    state.amendmentsLoading = false;
  },
  REMOVE_SCRATCH_ATTACHMENT(state, id) {
    const index = state.scratchAttachments.findIndex(x => x.id === id);
    if (index === -1) { return; }
    state.scratchAttachments.splice(index, 1);
  },
  UPDATE_SCRATCH_ATTACHMENT(state, data) {
    const index = state.scratchAttachments.findIndex(x => x.id === data.id);
    if (index === -1 || !data.props) { return; }
    const oldAttachment = state.scratchAttachments[index];
    const newAttachment = Object.assign({}, oldAttachment, data.props);
    state.scratchAttachments.splice(index, 1, newAttachment);
  },
  SET_SCRATCH_ATTACHMENTS(state, attachments) {
    if (attachments.length === 0) {
      state.scratchAttachments = [];
      return;
    }
    const uniqueAttachments = attachments.filter(a => !state.scratchAttachments.some(b => b.id === a.id));
    state.scratchAttachments = state.scratchAttachments.concat(uniqueAttachments);
  },
  SET_SCRATCH_SESSION(state, sessionId) {
    state.scratchSessionId = sessionId;
  },
  SET_AMENDMENTS_ERROR(state, error) {
    // not really in use yet
    state.amendmentsError = error;
    state.amendmentsLoading = false;
    state.amendmentsBusy = false;
    state.amendmentsSaving = false;
  },
  SET_NOTIFICATION(state, payload) {
    let notification = payload.notification;
    const componentNameOrFlag = payload.source;
    // Preserve existing oldTownGeocodeNum if it exists and new notification doesn't have it
    const existingLupcValue = state.notification ? state.notification.townGeocodeNum : null;
    const newLupcValue = notification.townGeocodeNum;
    if (componentNameOrFlag != 'btn') {
      state.notification = {
        oldTownGeocodeNum: newLupcValue !== null && newLupcValue !== undefined ? existingLupcValue : newLupcValue,
        ...notification,
      };      
    } else {
      state.notification = {
        ...notification,
      };      
    }
  },
  updateNotification(state, updates) {
    state.notification = { ...state.notification, ...updates };
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_TOWN_EXTENT(state, extent) {
    const buffer = olExtent.getHeight(extent) * 0.1;
    state.townExtent = olExtent.buffer(extent, buffer);
  },
  RESET_STATE(state) {
    Object.assign(state, defaultState());
  },
  SET_LANDOWNER(state, contact) {
    state.notification.landownerUser = contact;
    state.notification.landownerUserId = (contact || {}).id;
  },
  SET_LO_ERROR(state, error) {
    state.loError = error;
  },
  SET_OWNERSHIP(state, ownership) {
    state.notification.ownershipTypeId = ownership.ownershipTypeId;
    state.notification.ownershipAcresClassId = ownership.ownershipAcresClassId;
  },
  SET_DESIGNATED_AGENT(state, contact) {
    state.notification.designatedAgentUser = contact;
    state.notification.designatedAgentUserId = (contact || {}).id;
  },
  SET_DA_ERROR(state, error) {
    state.daError = error;
  },
  SET_OPERATOR(state, contact) {
    state.notification.operatorUser = contact;
    state.notification.operatorUserId = (contact || {}).id;
  },
  SET_OPERATOR_ERROR(state, error) {
    state.operatorError = error;
  },
  SET_LICENSED_FORESTER(state, contact) {
    state.notification.licensedForesterUser = contact;
    state.notification.licensedForesterUserId = (contact || {}).id;
  },
  SET_LF_ERROR(state, error) {
    state.lfError = error;
  },
  SET_IMPORTANT_REMINDERS(state, importantReminders) {
    state.importantReminders = importantReminders;
  },
  SET_ROLES(state, roles) {
    state.roles = roles;
  },
  SET_ERRORS(state, errors) {
    state.errors = errors;
  },
  SET_CERTIFICATIONS(state, certifications) {
    state.certifications = certifications;
  },
  SET_SHOW_CERTIFIED_MODAL(state, show) {
    state.showCertifiedModal = show;
  },
  SET_LANDOWNER_REPORTS(state, landownerReports) {
    state.landownerReports = landownerReports;
  },
  SET_LANDOWNER_REPORTS_LOADING(state, loading) {
    state.landownerReportsLoading = loading;
  },
  SET_FON_DELETE_ERROR(state, error) {
    if (error) {
      state.fonDeleteError = 'There was an error deleting this FON';
    } else {
      state.fonDeleteError = null;
    }
  },
};

const localGetters = {
  anyErrors(state) {
    return Object.values(state.errors).some(error => error.length);
  },
  draft(state) {
    return state.notification.statusId === 1;
  },
  pendingCertification(state) {
    return state.notification.statusId === 2;
  },
  active(state) {
    return state.notification.statusId === 3
      || state.notification.statusId === 5;
  },
  closed(state) {
    return state.notification.statusId === 4;
  },
  scheduled(state) {
    return state.notification.statusId === 6;
  },
  isLOManaged(state) {
    return state.notification.landownerUser
      && state.notification.landownerUser.isManagedForestUser;
  },
  isLUPCTown(state) {
    return state.notification.isLUPCTown;
  },
  isBeingCertifiedByDA(state) {
    return state.notification.designatedAgent
      && (state.notification.certificationMethodId === 2
        || state.notification.certificationMethodId === 3);
  },
};

export default {
  actions,
  mutations,
  getters: localGetters,
  state: localState,
  namespaced: true,
};
